<template>
  <div
    v-if="updateExists"
    :class="['pwa-update-prompt', { 'is-forced': forcedUpdate }]"
    dir="rtl"
    role="dialog"
    aria-modal="true"
  >
    <div class="pwa-update-content">
      <div class="pwa-update-heading">
        <img src="/assets/img/logo.png" alt="لوگوی بانک ایرانی" />
        <div>
          <strong>{{ forcedUpdate ? 'به‌روزرسانی الزامی است' : 'نسخه جدید آماده است' }}</strong>
          <p>{{ updateMessage }}</p>
          <small v-if="latestVersion">نسخه جدید: {{ latestVersion }}</small>
        </div>
      </div>
      <div class="pwa-update-actions">
        <button class="btn-update" :disabled="updating" @click="refreshApp">
          {{ updating ? 'در حال به‌روزرسانی...' : 'به‌روزرسانی' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { appReleaseAPI } from '../api'

export default {
  name: 'PwaUpdatePrompt',
  data () {
    return {
      updateExists: false,
      forcedUpdate: false,
      registration: null,
      latestVersion: null,
      updating: false
    }
  },
  computed: {
    updateMessage () {
      return this.forcedUpdate
        ? 'برای ادامه استفاده از ایرانی بانک باید نسخه جدید دریافت شود.'
        : 'برای دریافت آخرین تغییرات، برنامه را به‌روزرسانی کنید.'
    }
  },
  mounted () {
    document.addEventListener('sw-updated', this.showUpdatePrompt)
    this.checkRelease()
  },
  beforeUnmount () {
    document.removeEventListener('sw-updated', this.showUpdatePrompt)
  },
  methods: {
    compareVersions (current, minimum) {
      const normalize = value => String(value || '0').split('.').map(part => Number.parseInt(part, 10) || 0)
      const left = normalize(current)
      const right = normalize(minimum)
      const length = Math.max(left.length, right.length)

      for (let index = 0; index < length; index += 1) {
        if ((left[index] || 0) < (right[index] || 0)) return -1
        if ((left[index] || 0) > (right[index] || 0)) return 1
      }
      return 0
    },
    async checkRelease () {
      try {
        const response = await appReleaseAPI.getCurrent()
        const release = response.data?.data
        if (!release) return

        this.latestVersion = release.latest_version
        const currentVersion = process.env.VUE_APP_VERSION || '0.0.0'
        if (release.force_update_enabled && this.compareVersions(currentVersion, release.minimum_supported_version) < 0) {
          this.forcedUpdate = true
          this.updateExists = true
        }
      } catch (error) {
        console.warn('App release check was unavailable:', error)
      }
    },
    showUpdatePrompt (event) {
      this.registration = event.detail
      this.forcedUpdate = true
      this.updateExists = true
    },
    async clearApplicationCaches () {
      if (!('caches' in window)) return
      const keys = await caches.keys()
      await Promise.all(keys.map(key => caches.delete(key)))
    },
    async refreshApp () {
      if (this.updating) return
      this.updating = true

      try {
        await this.clearApplicationCaches()
        if (this.registration?.waiting) {
          let reloaded = false
          const reloadOnce = () => {
            if (reloaded) return
            reloaded = true
            window.location.reload()
          }

          navigator.serviceWorker.addEventListener('controllerchange', reloadOnce, { once: true })
          this.registration.waiting.postMessage({ type: 'SKIP_WAITING' })
          window.setTimeout(reloadOnce, 3000)
          return
        }
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.getRegistration()
          await registration?.update()
        }
        window.location.reload()
      } catch (error) {
        this.updating = false
        console.warn('App update was unavailable:', error)
      }
    }
  }
}
</script>

<style scoped>
.pwa-update-prompt {
  position: fixed;
  bottom: 20px;
  left: 20px;
  right: 20px;
  z-index: 9999;
  padding: 16px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, .15);
  font-family: 'IRANSansXFaNum-Light', sans-serif;
}

.pwa-update-prompt.is-forced {
  inset: 0;
  display: grid;
  place-items: center;
  border-radius: 0;
  background: rgba(24, 34, 55, .82);
  backdrop-filter: blur(8px);
}

.pwa-update-prompt.is-forced .pwa-update-content {
  width: min(92vw, 440px);
  padding: 24px;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 20px 60px rgba(0, 0, 0, .25);
}

.pwa-update-heading { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.pwa-update-heading img { width: 42px; height: 42px; flex: 0 0 auto; border-radius: 10px; }
.pwa-update-heading strong { display: block; color: #1f2f4d; margin-bottom: 5px; }
.pwa-update-heading p { margin: 0; color: #5f6d86; font-size: 14px; line-height: 1.8; }
.pwa-update-heading small { color: #71809b; }
.pwa-update-actions { display: flex; justify-content: flex-end; }
.btn-update { border: 0; border-radius: 9px; padding: 10px 22px; color: #fff; background: #5186dd; font-family: inherit; cursor: pointer; }
.btn-update:hover { background: #4073c9; }
.btn-update:disabled { opacity: .65; cursor: wait; }
</style>
